/// <amd-module name="Wit/UI/Components/WorkItemState/WorkItemState" />
import * as React from "react";
import { getId } from "VSS/Core/Util/Object";
import { Observer } from "VSSUI/Observer";
import { Tooltip } from "VSSUI/TooltipEx";
import { css } from "VSSUI/Util";
import { COLOR_TRANSPARENT, COLOR_WHITE, hexToRgb, parseColor } from "../../Common/ColorUtilities";
import { IWorkItemStateProps, WorkItemStateAndColor } from "./WorkItemState.Props";

const DefaultWorkItemStateColor = "5688E0";

/**
 * Component to display work item state value with the state color.
 */
export const WorkItemState: React.FunctionComponent<IWorkItemStateProps> = (props: IWorkItemStateProps): JSX.Element | null => {
    const { data, className, hideText } = props;
    if (!data) {
        // Render nothing if state and color data is not passed.
        return null;
    }

    const tooltipHostId = getId("work-item-state-color-tooltip");

    return (
        <Observer data={data}>
            {(props: { data: WorkItemStateAndColor }) => {
                const stateName = props.data.name;
                const fill = props.data.fill || DefaultWorkItemStateColor;
                const fillColor = parseColor(fill);
                const borderColor = getBorderColor(fill);

                // when state name is hidden, we need aria information on the circle
                const stateCircleAriaProps = hideText
                    ? {
                          "aria-label": stateName
                      }
                    : undefined;

                const stateCircle = (
                    <div
                        className={css("work-item-state-circle", !hideText && "withText")}
                        style={{ backgroundColor: fillColor, borderColor: borderColor }}
                        {...stateCircleAriaProps}
                    />
                );

                return (
                    <div className={css("wit-ui-control work-item-state", className)}>
                        {hideText ? (
                            <Tooltip id={tooltipHostId} text={stateName} addAriaDescribedBy={true}>
                                {stateCircle}
                            </Tooltip>
                        ) : (
                            stateCircle
                        )}
                        {!hideText && (
                            <Tooltip id={tooltipHostId} overflowOnly={true} text={stateName}>
                                <div className="work-item-state-name">
                                    <span>{stateName}</span>
                                </div>
                            </Tooltip>
                        )}
                    </div>
                );
            }}
        </Observer>
    );
};

function getBorderColor(stateFillColor: string): string {
    const border = stateFillColor === COLOR_WHITE || stateFillColor === COLOR_TRANSPARENT ? DefaultWorkItemStateColor : stateFillColor;
    return parseStateBorderColor(border);
}

function parseStateBorderColor(rawColor: string) {
    if (rawColor.startsWith("var(")) {
        return `rgba(${rawColor}, 0.3)`;
    } else {
        const borderColorHex = parseColor(rawColor);
        const borderColorRGB = hexToRgb(borderColorHex);
        return `rgba(${borderColorRGB[0]}, ${borderColorRGB[1]}, ${borderColorRGB[2]}, 0.3)`;
    }
}
